import React, { useEffect, useRef } from 'react';
import { Dialog as PrimeDialog, DialogProps as PrimeDialogProps } from 'primereact/dialog';

export interface DialogProps extends PrimeDialogProps {
  /**
   * Additional class applied to the dialog header region.
   * Consumed by tailwind themes via `props.headerClassName`.
   */
  headerClassName?: string;

  /**
   * Additional class applied to the dialog content region.
   * Consumed by tailwind themes via `props.contentClassName`.
   */
  contentClassName?: string;

  /**
   * Additional class applied to the dialog footer region.
   * Consumed by tailwind themes via `props.footerClassName`.
   */
  footerClassName?: string;

  /**
   * Additional class applied to the dialog header icons region.
   * Consumed by tailwind themes via `props.headerIconsClassName`.
   */
  headerIconsClassName?: string;
}

/** Elements that can receive keyboard focus inside the trapped dialog. */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

const getFocusableElements = (container: HTMLElement) =>
  Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) => element.getAttribute('aria-hidden') !== 'true',
  );

/**
 * PrimeReact's focus sentinels are `aria-hidden` but still tabbable. Remove them; the keydown
 * handler below wraps focus.
 */
const neutraliseFocusTrapSentinels = (dialogElement: HTMLElement | null) => {
  dialogElement?.querySelectorAll<HTMLElement>('[data-p-hidden-focusable="true"]').forEach((element) => {
    element.removeAttribute('aria-hidden');
    element.tabIndex = -1;
  });
};

export const Dialog: React.FC<DialogProps> = (props) => {
  const dialogRef = useRef<PrimeDialog>(null);
  const { visible } = props;

  useEffect(() => {
    if (!visible) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') {
        return;
      }

      const dialogElement = dialogRef.current?.getElement();
      if (!dialogElement?.contains(document.activeElement)) {
        return;
      }

      const focusable = getFocusableElements(dialogElement);
      if (focusable.length === 0) {
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    // Capture phase: the dialog's close button stops non-Escape keys on bubble, which would
    // otherwise swallow Tab before this listener saw it.
    document.addEventListener('keydown', handleKeyDown, true);

    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [visible]);

  // PrimeReact returns focus only when `visible` turns false, not when the dialog unmounts while open.
  useEffect(() => {
    if (!visible) {
      return;
    }

    const opener = document.activeElement;
    const dialog = dialogRef.current;

    return () => {
      if (!(opener instanceof HTMLElement) || opener === document.body || !opener.isConnected) {
        return;
      }

      const activeElement = document.activeElement;
      const dialogElement = dialog?.getElement();
      if (!activeElement || activeElement === document.body || dialogElement?.contains(activeElement)) {
        opener.focus();
      }
    };
  }, [visible]);

  return (
    <PrimeDialog
      ref={dialogRef}
      {...props}
      onShow={() => {
        neutraliseFocusTrapSentinels(dialogRef.current?.getElement() ?? null);
        props.onShow?.();
      }}
    />
  );
};
